import React, { useState, useEffect, useCallback } from 'react';
import { adminAPI } from '../../lib/api';
import { format } from 'date-fns';
import { toast } from 'sonner';
import {
  Shield, Search, RefreshCw, ChevronDown, ChevronRight, AlertOctagon,
  CheckCircle, XCircle, AlertTriangle, Clock, Filter, Eye, FileJson
} from 'lucide-react';

// UI Components
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';

const AdminPurgeAudit = () => {
  // State
  const [auditRows, setAuditRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 25,
    total: 0,
    totalPages: 1
  });
  
  // Filters
  const [filters, setFilters] = useState({
    tenantId: '',
    actorEmail: '',
    result: '',
    action: '',
    days: '30'
  });
  
  // Stats
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  
  // Detail modal
  const [detailModal, setDetailModal] = useState({ open: false, data: null });
  const [expandedRow, setExpandedRow] = useState(null);

  // Fetch audit data
  const fetchAuditData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      if (filters.tenantId) params.tenant_id = filters.tenantId;
      if (filters.actorEmail) params.actor_email = filters.actorEmail;
      if (filters.result) params.result = filters.result;
      if (filters.action) params.action = filters.action;
      if (filters.days) params.days = parseInt(filters.days);
      
      const response = await adminAPI.getPurgeAudit(params);
      setAuditRows(response.data.rows);
      setPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (err) {
      toast.error('Failed to load purge audit data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filters]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const response = await adminAPI.getPurgeAuditStats(parseInt(filters.days) || 30);
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    } finally {
      setStatsLoading(false);
    }
  }, [filters.days]);

  // Initial load
  useEffect(() => {
    fetchAuditData();
    fetchStats();
  }, [fetchAuditData, fetchStats]);

  // Get result badge
  const getResultBadge = (result) => {
    const variants = {
      success: { variant: 'default', className: 'bg-green-100 text-green-800', icon: CheckCircle },
      allowed: { variant: 'secondary', className: 'bg-blue-100 text-blue-800', icon: Eye },
      blocked: { variant: 'outline', className: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
      failed: { variant: 'destructive', className: 'bg-red-100 text-red-800', icon: XCircle }
    };
    
    const config = variants[result] || variants.blocked;
    const Icon = config.icon;
    
    return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {result}
      </Badge>
    );
  };

  // Get action badge
  const getActionBadge = (action) => {
    if (action === 'execute') {
      return <Badge className="bg-red-100 text-red-800"><AlertOctagon className="w-3 h-3 mr-1" /> execute</Badge>;
    }
    return <Badge variant="secondary"><Eye className="w-3 h-3 mr-1" /> preview</Badge>;
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      tenantId: '',
      actorEmail: '',
      result: '',
      action: '',
      days: '30'
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // View detail
  const viewDetail = async (auditId) => {
    try {
      const response = await adminAPI.getPurgeAuditDetail(auditId);
      setDetailModal({ open: true, data: response.data });
    } catch (err) {
      toast.error('Failed to load audit detail');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6 text-red-600" />
            Purge Audit Trail
          </h1>
          <p className="text-muted-foreground mt-1">
            Track all tenant purge attempts for debugging and compliance
          </p>
        </div>
        <Button onClick={() => { fetchAuditData(); fetchStats(); }} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-muted-foreground">Total Entries ({filters.days || 30}d)</div>
              <div className="text-2xl font-bold">{stats.total_entries}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-green-600">Successful Purges</div>
              <div className="text-2xl font-bold text-green-700">{stats.result_counts?.success || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-yellow-600">Blocked Attempts</div>
              <div className="text-2xl font-bold text-yellow-700">{stats.result_counts?.blocked || 0}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-sm text-red-600">Failed Purges</div>
              <div className="text-2xl font-bold text-red-700">{stats.result_counts?.failed || 0}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3 flex-wrap">
            <Input
              placeholder="Tenant ID"
              value={filters.tenantId}
              onChange={(e) => handleFilterChange('tenantId', e.target.value)}
              className="w-48"
            />
            <Input
              placeholder="Actor Email"
              value={filters.actorEmail}
              onChange={(e) => handleFilterChange('actorEmail', e.target.value)}
              className="w-48"
            />
            <Select value={filters.result || "all"} onValueChange={(v) => handleFilterChange('result', v === "all" ? '' : v)}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Result" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Results</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="allowed">Allowed</SelectItem>
                <SelectItem value="blocked">Blocked</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.action || "all"} onValueChange={(v) => handleFilterChange('action', v === "all" ? '' : v)}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="preview">Preview</SelectItem>
                <SelectItem value="execute">Execute</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.days} onValueChange={(v) => handleFilterChange('days', v)}>
              <SelectTrigger className="w-36">
                <SelectValue placeholder="Time Range" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">Last 7 days</SelectItem>
                <SelectItem value="30">Last 30 days</SelectItem>
                <SelectItem value="90">Last 90 days</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={clearFilters}>Clear</Button>
            <Button onClick={fetchAuditData}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Audit Table */}
      <Card>
        <CardHeader>
          <CardTitle>Audit Entries</CardTitle>
          <CardDescription>
            {pagination.total} entries found
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Timestamp</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Tenant</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Result</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead className="w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditRows.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow className="cursor-pointer hover:bg-muted/50">
                    <TableCell>
                      {row.deleted_counts && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="p-0 h-6 w-6"
                          onClick={() => setExpandedRow(expandedRow === row.id ? null : row.id)}
                        >
                          {expandedRow === row.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs">
                      {row.created_at ? format(new Date(row.created_at), 'MMM d, HH:mm:ss') : '-'}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{row.actor_email || '-'}</div>
                      {row.actor_ip && <div className="text-xs text-muted-foreground">{row.actor_ip}</div>}
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">{row.tenant_name || '-'}</div>
                      {row.tenant_id && <div className="text-xs text-muted-foreground font-mono">{row.tenant_id.substring(0, 8)}...</div>}
                    </TableCell>
                    <TableCell>{getActionBadge(row.action)}</TableCell>
                    <TableCell>{getResultBadge(row.result)}</TableCell>
                    <TableCell className="text-sm">
                      {row.blocked_reason || row.error_message || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {row.duration_ms ? `${row.duration_ms}ms` : '-'}
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => viewDetail(row.id)}>
                        <FileJson className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded row for deleted_counts */}
                  {expandedRow === row.id && row.deleted_counts && (
                    <TableRow>
                      <TableCell colSpan={9} className="bg-muted/30 p-4">
                        <div className="text-sm font-medium mb-2">Deleted Counts:</div>
                        <div className="grid grid-cols-6 gap-2 text-xs">
                          {Object.entries(row.deleted_counts)
                            .filter(([_, count]) => count > 0)
                            .map(([table, count]) => (
                              <div key={table} className="flex justify-between bg-card rounded px-2 py-1 border">
                                <span className="text-muted-foreground">{table}:</span>
                                <span className="font-medium text-green-700">{count}</span>
                              </div>
                            ))}
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
              
              {auditRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No audit entries found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                Page {pagination.page} of {pagination.totalPages}
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page <= 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={detailModal.open} onOpenChange={(open) => !open && setDetailModal({ open: false, data: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Audit Entry Detail</DialogTitle>
          </DialogHeader>
          {detailModal.data && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-muted-foreground">ID</div>
                  <div className="font-mono text-sm">{detailModal.data.id}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Timestamp</div>
                  <div>{detailModal.data.created_at ? format(new Date(detailModal.data.created_at), 'PPpp') : '-'}</div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="text-sm font-medium mb-2">Actor</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Email:</span> {detailModal.data.actor?.email || '-'}</div>
                  <div><span className="text-muted-foreground">User ID:</span> {detailModal.data.actor?.user_id || '-'}</div>
                  <div><span className="text-muted-foreground">IP:</span> {detailModal.data.actor?.ip || '-'}</div>
                  <div className="col-span-2"><span className="text-muted-foreground">User Agent:</span> {detailModal.data.actor?.user_agent || '-'}</div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="text-sm font-medium mb-2">Tenant</div>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-muted-foreground">Name:</span> {detailModal.data.tenant?.name || '-'}</div>
                  <div><span className="text-muted-foreground">ID:</span> {detailModal.data.tenant?.id || '-'}</div>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <div className="text-sm font-medium mb-2">Result</div>
                <div className="flex gap-4 items-center">
                  {getActionBadge(detailModal.data.action)}
                  {getResultBadge(detailModal.data.result)}
                  {detailModal.data.blocked_reason && (
                    <span className="text-sm text-yellow-700">Reason: {detailModal.data.blocked_reason}</span>
                  )}
                  {detailModal.data.duration_ms && (
                    <span className="text-sm text-muted-foreground flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {detailModal.data.duration_ms}ms
                    </span>
                  )}
                </div>
              </div>
              
              {detailModal.data.error && (
                <div className="border-t pt-4">
                  <div className="text-sm font-medium mb-2 text-red-600">Error</div>
                  <div className="bg-red-50 p-3 rounded text-sm">
                    <div><span className="text-muted-foreground">Class:</span> {detailModal.data.error.class}</div>
                    <div><span className="text-muted-foreground">Message:</span> {detailModal.data.error.message}</div>
                  </div>
                </div>
              )}
              
              {detailModal.data.deleted_counts && (
                <div className="border-t pt-4">
                  <div className="text-sm font-medium mb-2">Deleted Counts</div>
                  <pre className="bg-muted p-3 rounded text-xs overflow-auto max-h-48">
                    {JSON.stringify(detailModal.data.deleted_counts, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPurgeAudit;
